import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { endpoints, ID } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { QUERY_KEY_LAZY_QUEUES } from '../../../business/queues/useLazyQueues';
import { QUERY_KEY_QUEUES_UNPAGINATED } from '../../../business/queues/useUnpaginatedQueues';
import { api } from '../../../lib/apiClient';
import { notify } from '../../toaster';

type SetQueueEnabledPayload = { queueId: ID; enabled: boolean };

export const useSetQueueEnabled = () => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { mutate, ...mutation } = useMutation({
    mutationFn: ({ queueId, enabled }: SetQueueEnabledPayload) =>
      api.request(endpoints.queues.patch(queueId, { enabled })),

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_QUEUES_UNPAGINATED],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_LAZY_QUEUES],
      });
    },

    onError: () => {
      notify.error({
        title: intl.formatMessage({ id: 'containers.message.queueError' }),
      });
    },
  });

  const setQueueEnabled = useCallback(
    (payload: SetQueueEnabledPayload) => {
      queryClient.setQueriesData<Queue[] | undefined>(
        [QUERY_KEY_QUEUES_UNPAGINATED],
        prev =>
          prev?.map(q =>
            q.id === payload.queueId ? { ...q, enabled: payload.enabled } : q
          )
      );
      mutate(payload);
    },
    [mutate, queryClient]
  );

  return { ...mutation, mutate: setQueueEnabled };
};
